import { useState, useEffect } from 'react';

// Confetti animation component for quiz correct answers
export function Confetti({ trigger }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!trigger) return;
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4'];
    const newParticles = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -10,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 8 + 4,
      delay: Math.random() * 0.5,
      duration: Math.random() * 1.5 + 1,
      drift: (Math.random() - 0.5) * 60,
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => setParticles([]), 3000);
    return () => clearTimeout(timer);
  }, [trigger]);

  if (particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50" data-testid="confetti">
      {particles.map(p => (
        <div key={p.id} className="absolute rounded-sm" style={{
          left: `${p.x}%`, width: p.size, height: p.size, backgroundColor: p.color,
          animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
          '--drift': `${p.drift}px`,
        }} />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) translateX(var(--drift)) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Knowledge Tree - visual progress indicator
export function KnowledgeTree({ topicsCompleted = 0, totalTopics = 20 }) {
  const progress = totalTopics ? Math.min(100, Math.round((topicsCompleted / totalTopics) * 100)) : 0;
  const leaves = Math.min(12, Math.floor(topicsCompleted / 2));
  const treeColor = progress >= 80 ? '#10B981' : progress >= 50 ? '#22C55E' : progress >= 25 ? '#84CC16' : '#94A3B8';

  return (
    <div className="flex flex-col items-center" data-testid="knowledge-tree">
      <svg width="120" height="140" viewBox="0 0 120 140">
        {/* Trunk */}
        <rect x="53" y="80" width="14" height="55" rx="3" fill="#8B6914" />
        <rect x="55" y="80" width="10" height="55" rx="2" fill="#A0782C" />
        {/* Ground */}
        <ellipse cx="60" cy="135" rx="35" ry="6" fill="#D4C4A0" opacity="0.5" />
        {/* Canopy layers */}
        <ellipse cx="60" cy="60" rx={20 + progress * 0.2} ry={18 + progress * 0.15} fill={treeColor} opacity="0.3">
          <animate attributeName="rx" values={`${18 + progress * 0.2};${22 + progress * 0.2};${18 + progress * 0.2}`} dur="4s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="60" cy="50" rx={16 + progress * 0.18} ry={14 + progress * 0.12} fill={treeColor} opacity="0.5" />
        <ellipse cx="60" cy="42" rx={12 + progress * 0.15} ry={10 + progress * 0.1} fill={treeColor} opacity="0.7" />
        {/* Leaves */}
        {Array.from({ length: leaves }).map((_, i) => {
          const angle = (i / leaves) * Math.PI * 2;
          const r = 22 + Math.random() * 8;
          const cx = 60 + Math.cos(angle) * r;
          const cy = 52 + Math.sin(angle) * (r * 0.7);
          return <circle key={i} cx={cx} cy={cy} r="4" fill={treeColor} opacity="0.8">
            <animate attributeName="r" values="3.5;4.5;3.5" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>;
        })}
        {/* Progress text */}
        <text x="60" y="16" textAnchor="middle" className="font-heading" fontSize="11" fill="#334155" fontWeight="bold">{`${progress}%`}</text>
      </svg>
      <p className="text-[10px] text-slate-500 font-body mt-1">{topicsCompleted} of {totalTopics} topics</p>
    </div>
  );
}

// Aha! Summary Card - session-end encouragement
export function AhaSummaryCard({ session, onClose }) {
  if (!session) return null;

  const messages = [
    "You're building real understanding! Keep this momentum going.",
    "Every question you ask makes you smarter. Amazing session!",
    "Your curiosity is your superpower. See you next time!",
    "You covered great ground today. Your future self will thank you!",
    "That was a solid study session. You're closer to mastery!",
  ];
  const randomMsg = messages[Math.floor(Math.random() * messages.length)];

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 flex items-center justify-center p-4 animate-fade-in" data-testid="aha-summary">
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-slide-up">
        <div className="text-center mb-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-3">
            <span className="text-2xl">&#x2728;</span>
          </div>
          <h3 className="font-heading text-lg font-bold text-slate-800">Session Complete!</h3>
          <p className="text-xs text-slate-400 font-body mt-1">{session.subject} · {session.topic}</p>
        </div>
        <p className="text-sm text-slate-600 font-body text-center mb-4 leading-relaxed">{randomMsg}</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="font-heading text-lg font-bold text-blue-600">{session.message_count || 0}</p>
            <p className="text-[10px] text-slate-400 font-body">Messages</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="font-heading text-lg font-bold text-emerald-600">+{(session.message_count || 1) * 5}</p>
            <p className="text-[10px] text-slate-400 font-body">Veda Coins Earned</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="aha-close-btn"
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full font-heading text-sm hover:shadow-lg transition-all">
          Keep Learning
        </button>
      </div>
    </div>
  );
}

// Animated Streak Badge
export function StreakBadge({ days }) {
  const milestones = [7, 14, 30, 60, 100, 365];
  const nextMilestone = milestones.find(m => m > days) || 365;
  const progress = (days / nextMilestone) * 100;

  return (
    <div className="flex items-center gap-2" data-testid="streak-badge">
      <div className="relative w-10 h-10">
        <svg className="w-10 h-10 -rotate-90">
          <circle cx="20" cy="20" r="16" fill="none" stroke="#F1F5F9" strokeWidth="3" />
          <circle cx="20" cy="20" r="16" fill="none" stroke="#F59E0B" strokeWidth="3"
            strokeDasharray={`${progress} ${100 - progress}`} strokeLinecap="round"
            className="transition-all duration-1000" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-heading font-bold text-amber-600">{days}</span>
      </div>
      <div>
        <p className="text-xs font-heading font-semibold text-slate-700">{days} Day Streak</p>
        <p className="text-[10px] text-slate-400 font-body">{nextMilestone - days} more to {nextMilestone}-day badge</p>
      </div>
    </div>
  );
}

// Shareable Streak Card (for WhatsApp/Instagram)
export function ShareableStreakCard({ user, streakDays, onClose }) {
  const canvasRef = null; // Would use html2canvas for actual sharing

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="shareable-streak">
      <div className="bg-white rounded-3xl overflow-hidden max-w-sm w-full shadow-2xl animate-slide-up">
        {/* Card to share */}
        <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 p-6 text-white text-center" data-testid="streak-share-card">
          <p className="text-xs font-body opacity-80 mb-1">Gurukul Veda</p>
          <div className="w-20 h-20 mx-auto rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3">
            <span className="font-heading text-3xl font-bold">{streakDays}</span>
          </div>
          <h3 className="font-heading text-xl font-bold mb-1">{streakDays} Day Streak!</h3>
          <p className="text-sm font-body opacity-90">{user?.name || 'Student'} is on fire!</p>
          <div className="flex justify-center gap-4 mt-3">
            {[7, 14, 30, 60].map(m => (
              <div key={m} className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-heading font-bold ${streakDays >= m ? 'bg-white text-blue-600' : 'bg-white/20 text-white/60'}`}>
                {m}
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 flex gap-2">
          <button className="flex-1 py-2.5 bg-green-500 text-white rounded-full font-heading text-sm hover:bg-green-600 transition-all" data-testid="share-whatsapp">
            Share on WhatsApp
          </button>
          <button onClick={onClose} className="px-4 py-2.5 bg-slate-100 text-slate-600 rounded-full font-heading text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
